/**
 * src/game/progression/meta-economy.ts — 永久 meta 商店「金幣經濟」純函式層.
 *
 * Spec: docs/spec/LONG_TERM_PROGRESSION.md §4(金幣經濟,解擔憂③「錢太多就沒取捨」).
 *
 * 雙軌進度鐵則 (CLAUDE.md 最高優先 / LONG_TERM_PROGRESSION §6 / commander.ts NEVER 規範):
 *   - meta 金幣 = **玩遊戲軌**貨幣。本模組 **NEVER** 讀/寫
 *     factionXP / freeXP / skillLevels / 傷害 / HP / 冷卻 / 修煉值。
 *   - 學習獎勵是另一份 spec(docs/spec/LEARNING_REWARDS.md,未定);這裡只留
 *     `learningBonus` 鉤(預設 0、不實作)。
 *
 * 設計核心(§4):**任何時刻你能買的,遠少於『全買』 → 永遠在取捨 → 專精 → 差異性。**
 *   - 產出刻意不慷慨(每場中等 + 微幅隨清關/難度旗幟)。
 *   - 成本指數成長(Brotato 實證:同項第 n 級 = cost1 × growth^(n-1),初始 ~200,越後越貴)。
 *   - respec 收手續費(鼓勵嘗試但不無痛全切)。
 *
 * 純函式、無 Phaser、無 I/O。caller(結算/商店 UI)負責讀寫 PlayerProgress + saveProgress。
 */

// ── 金幣產出(§4「產出」)──────────────────────────────────────────────────
//
// 刻意不慷慨。對齊 VS/Brotato:一場中等產出,微幅隨「清到第幾關」與「難度旗幟」加成。
// 與守門測試(meta-economy.test.ts:前 20 場 < 任一英雄整棵樹成本)綁死,改這裡要回看測試。

/** 一場戰鬥的基礎 meta 金幣產出(中等;刻意不慷慨)。 */
export const META_COIN_BASE = 30;

/** 每清到第 N 關的微幅加成(線性,× stageReached)。 */
export const META_COIN_PER_STAGE = 5;

/** 通關(stageCleared)額外一次性加成。 */
export const META_COIN_CLEAR_BONUS = 10;

/**
 * 難度旗幟倍率(ROGUELITE_ROADMAP §P1.5:cozy/spicy/feisty)。
 * 只放大金幣產出(玩遊戲軌獎勵),不碰任何戰力。預設 cozy=1。
 */
export const META_COIN_TIER_MULT: Readonly<Record<string, number>> = {
  cozy: 1,
  spicy: 1.25,
  feisty: 1.5,
};

/** 單場戰績(meta 金幣產出輸入;與 commander CommanderBattleStats 同源,但只取我們要的)。 */
export interface MetaCoinRunStats {
  /** 抵達/清到的關卡編號(>=1)。微幅加成用。 */
  stageReached?: number;
  /** 是否通關(額外一次性加成)。 */
  stageCleared?: boolean;
  /** 難度旗幟 id('cozy'|'spicy'|'feisty');未知 → cozy(×1)。 */
  difficultyTier?: string;
}

/**
 * 一場戰鬥產出多少 meta 金幣(§4「產出」)。純由遊戲表現 + 難度旗幟決定,
 * **不乘任何學習係數**。刻意不慷慨,逼專精。
 *   coins = round((BASE + stageReached*PER_STAGE + cleared*CLEAR_BONUS) × tierMult)
 * 一場普通(stage1、未通關、cozy)≈ 35;通關高關 + feisty 旗幟也只是微幅放大。
 */
export function computeMetaCoins(stats: MetaCoinRunStats | undefined): number {
  const stage = stats && Number.isFinite(stats.stageReached) && (stats.stageReached as number) > 0
    ? Math.floor(stats.stageReached as number) : 1;
  const cleared = !!(stats && stats.stageCleared);
  const tier = stats?.difficultyTier;
  const tierMult = (tier && META_COIN_TIER_MULT[tier]) || 1;
  const base = META_COIN_BASE + stage * META_COIN_PER_STAGE + (cleared ? META_COIN_CLEAR_BONUS : 0);
  return Math.max(0, Math.round(base * tierMult));
}

// ── 指數成本(§4「成本指數成長」)─────────────────────────────────────────
//
// Brotato/VS 實證:同一項第 n 級成本 = cost1 × growth^(n-1)。VS Max Health 初始 200、指數漲。
// 我們各節點自帶 cost1(catalog 定義),growth 全域共用(可單測)。

/** 指數成本成長率(每升一級成本 ×此倍率;Brotato/VS 風,>1)。 */
export const META_COST_GROWTH = 1.6;

/**
 * 某節點第 `nextLevel` 級(1-based:買第 1 級傳 1、第 2 級傳 2…)的成本。
 *   cost(n) = round(cost1 × growth^(n-1))
 * nextLevel <= 0 視為 1(防呆);cost1 非正 → 0(免費,理論上 catalog 不會這樣設)。
 */
export function metaNodeCostAtLevel(cost1: number, nextLevel: number, growth: number = META_COST_GROWTH): number {
  const base = Number.isFinite(cost1) && cost1 > 0 ? cost1 : 0;
  if (base <= 0) return 0;
  const n = Number.isFinite(nextLevel) && nextLevel > 1 ? Math.floor(nextLevel) : 1;
  const g = Number.isFinite(growth) && growth > 0 ? growth : META_COST_GROWTH;
  return Math.round(base * Math.pow(g, n - 1));
}

/**
 * 把某節點從 `currentLevel` 買滿到 `maxLevel` 的「累計總成本」(§4 總量設計用)。
 * 用於商店顯示「買滿這項還要 X 金」+ 守門測試「整棵樹成本」。
 */
export function metaNodeTotalCost(
  cost1: number,
  currentLevel: number,
  maxLevel: number,
  growth: number = META_COST_GROWTH,
): number {
  const from = Number.isFinite(currentLevel) && currentLevel > 0 ? Math.floor(currentLevel) : 0;
  const to = Number.isFinite(maxLevel) && maxLevel > 0 ? Math.floor(maxLevel) : 0;
  let total = 0;
  for (let lv = from + 1; lv <= to; lv++) {
    total += metaNodeCostAtLevel(cost1, lv, growth);
  }
  return total;
}

// ── respec(§4「取捨摩擦」)────────────────────────────────────────────────
//
// 可洗點但收手續費。退還「已花成本」的一部分(rate),手續費 = 已花成本 × (1 - rate)。
// 鼓勵嘗試,但不無痛全切(全額退會讓「全買 → 看哪個好 → 退掉重來」沒成本)。

/** respec 退款率(退還已花成本的比例;1 - 此值 = 手續費比例)。 */
export const META_RESPEC_REFUND_RATE = 0.7;

/**
 * 退掉某棵樹(已花 `spentCoins`)能拿回多少金幣(收手續費後)。
 *   refund = floor(spentCoins × REFUND_RATE)
 * 手續費 = spentCoins - refund。spentCoins 非正 → 0。
 */
export function metaRespecRefund(spentCoins: number, refundRate: number = META_RESPEC_REFUND_RATE): number {
  const spent = Number.isFinite(spentCoins) && spentCoins > 0 ? Math.floor(spentCoins) : 0;
  const rate = Number.isFinite(refundRate) && refundRate >= 0 && refundRate <= 1 ? refundRate : META_RESPEC_REFUND_RATE;
  return Math.floor(spent * rate);
}
